import { Link } from 'react-router';
import { toast } from 'sonner';
import { telechargerPilotagePaie, usePilotagePaie } from '@/api/hooks';
import type { ResumePaiePilotage } from '@/api/types';
import { Chargement, ErreurBloc, Kpi, PageHeader, Vide } from '@/components/common';
import { StatutCalculBadge } from '@/components/calcul-paie';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn, dateFr, montant, nombre } from '@/lib/utils';

const usd = (n: number | null | undefined) => montant(n, true);
const usd0 = (n: number | null | undefined) => montant(n, false);

/** Pilotage de la paie (lot HP-6, § 8.4) : effectifs et mouvements, masse salariale et coût employeur sur douze mois, obligations, progression du circuit, qualité des dossiers et des fiches. */
export function PilotagePaie() {
  const { data, isLoading, error } = usePilotagePaie();
  if (isLoading) return <Chargement />;
  if (error || !data) return <ErreurBloc erreur={error} />;
  const d = data.derniere;
  const o = d?.obligations ?? null;
  const v = d?.variation ?? null;
  const pct = (x: number | null | undefined) => (x === null || x === undefined ? '' : `${x >= 0 ? '+' : ''}${nombre(x, 1)} %`);
  return (
    <div className="space-y-6">
      <PageHeader
        titre="Pilotage de la paie"
        sousTitre={`Au ${dateFr(data.jour)} · calculs réels (simulations exclues) · montants en USD. Le contrôle de gestion, la comptabilité et la trésorerie lisent la masse salariale sans salaire individuel.`}
        actions={
          <Button variant="outline" onClick={() => telechargerPilotagePaie().catch((e) => toast.error(e instanceof Error ? e.message : 'Export impossible.'))}>
            Exporter (Excel)
          </Button>
        }
      />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Agents payés" valeur={data.effectifs.payes} detail={`${data.effectifs.actifs} actif(s) · ${data.effectifs.horsPaie} hors paie barémique · ${data.effectifs.entrees30j} entrée(s), ${data.effectifs.sorties30j} sortie(s) sur trente jours`} />
        <Kpi label={d ? `Net à payer · ${d.mois}` : 'Net à payer'} valeur={d ? usd(d.netAPayer) : '—'} detail={d ? `${d.bulletins} bulletin(s) · ${d.exclus} exclu(s) · ${d.libelleStatut}${v ? ` · ${pct(v.net.pct)} vs ${v.mois}` : ''}` : 'aucun calcul réel'} accent={d?.statut === 'CLOTUREE' ? 'ok' : undefined} />
        <Kpi label="Coût employeur" valeur={d ? usd(d.coutEmployeur) : '—'} detail={d ? `charges patronales ${usd(d.patronales)}${v ? ` · ${pct(v.coutEmployeur.pct)} vs ${v.mois}` : ''}` : ''} />
        <Kpi label="À reverser (CNSS, INPP, ONEM, impôt)" valeur={o ? usd(o.total) : '—'} detail={o ? `CNSS ${usd(o.cnssTotal)} · impôt ${usd(o.iprUsd)} (${nombre(o.iprCdf, 0)} CDF)` : ''} accent={o ? 'alerte' : undefined} />
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {d?.etape ? (
          <Link to={`/paie/calculs?calcul=${d.id}`}>
            <Kpi label="Étape du circuit" valeur={d.etape.libelle.split(' (')[0]} detail={`attendue de : ${d.etape.attendueDe}${d.etape.moi ? ' · votre décision est attendue' : ''}`} accent={d.etape.moi ? 'alerte' : undefined} />
          </Link>
        ) : (
          <Kpi label="Étape du circuit" valeur={d ? d.libelleStatut : '—'} detail={d?.clotureLe ? `clôturée le ${dateFr(d.clotureLe)}` : ''} accent={d?.statut === 'CLOTUREE' ? 'ok' : undefined} />
        )}
        <Link to={data.periode ? `/paie/presences?mois=${data.periode.mois}&onglet=validation` : '/paie/presences'}>
          <Kpi label={data.periode ? `Présences · ${data.periode.mois}` : 'Présences'} valeur={data.periode ? `${data.periode.structuresValidees} / ${data.periode.structures}` : '—'} detail={data.periode ? `structures validées · période ${data.periode.statut === 'OUVERTE' ? 'ouverte' : 'clôturée'} · ${data.periode.variablesSaisies} variable(s) à valider` : 'aucune période'} accent={data.periode && data.periode.structuresValidees < data.periode.structures ? 'alerte' : 'ok'} />
        </Link>
        <Link to="/paie/remunerations?statut=A_REGULARISER">
          <Kpi label="Fiches de référence" valeur={`${data.dossiers.fichesApprouvees} / ${data.dossiers.fiches}`} detail={`approuvées · ${data.dossiers.fichesARegulariser} à régulariser · ${data.dossiers.agentsSansFiche} agent(s) sans fiche`} accent={data.dossiers.fichesApprouvees < data.effectifs.payes ? 'alerte' : 'ok'} />
        </Link>
        <Link to="/rh/hrms">
          <Kpi label="Dossiers HRMS" valeur={`${data.dossiers.valides} / ${data.dossiers.total}`} detail={`validés · ${data.dossiers.controles} contrôlé(s) · ${data.dossiers.aRegulariser} à régulariser · ${data.effectifs.finsContratCdd90j} fin(s) de CDD dans 90 jours`} accent={data.dossiers.aRegulariser > 0 ? 'alerte' : 'ok'} />
        </Link>
      </div>
      <Card data-testid="pilotage-paies">
        <CardHeader>
          <CardTitle>Masse salariale des douze derniers mois</CardTitle>
          <CardDescription>Net à payer et coût employeur par mois de rémunération ; {data.simulations} simulation(s) et {data.comparaisons} paie(s) de comparaison hors graphique.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!data.paies.length ? (
            <Vide message="Aucun calcul réel : lancez le calcul d'une période clôturée." />
          ) : (
            <>
              <Barres paies={data.paies} />
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Mois</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="hidden md:table-cell text-right">Bulletins</TableHead>
                    <TableHead className="hidden lg:table-cell text-right">Brut de période</TableHead>
                    <TableHead className="hidden md:table-cell text-right">Gains</TableHead>
                    <TableHead className="hidden lg:table-cell text-right">CNSS</TableHead>
                    <TableHead className="hidden lg:table-cell text-right">Impôt</TableHead>
                    <TableHead className="text-right">Net à payer</TableHead>
                    <TableHead className="text-right">Coût employeur</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...data.paies].reverse().map((c) => (
                    <TableRow key={c.id}>
                      <TableCell className="font-medium">
                        <Link to={`/paie/calculs?calcul=${c.id}`} className="text-primary hover:underline">
                          {c.mois}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <StatutCalculBadge statut={c.statut} simulation={false} />
                        {c.etape && <div className="text-xs text-muted-foreground">{c.etape.attendueDe}</div>}
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-right">
                        {c.bulletins}
                        {c.exclus > 0 && <div className="text-xs text-muted-foreground">{c.exclus} exclu(s)</div>}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-right">{usd0(c.brutPeriode)}</TableCell>
                      <TableCell className="hidden md:table-cell text-right">{usd0(c.totalGains)}</TableCell>
                      <TableCell className="hidden lg:table-cell text-right">{usd0(c.cnss)}</TableCell>
                      <TableCell className="hidden lg:table-cell text-right">{usd0(c.impotUsd)}</TableCell>
                      <TableCell className="text-right font-medium">{usd0(c.netAPayer)}</TableCell>
                      <TableCell className="text-right">{usd0(c.coutEmployeur)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </CardContent>
      </Card>
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Masse salariale par structure{d ? ` · ${d.mois}` : ''}</CardTitle>
            <CardDescription>Coût employeur de la dernière paie calculée et part de chaque structure.</CardDescription>
          </CardHeader>
          <CardContent>
            {!d?.parStructure.length ? (
              <Vide message="Aucune paie calculée." />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Structure</TableHead>
                    <TableHead className="text-right">Agents</TableHead>
                    <TableHead className="hidden md:table-cell text-right">Net à payer</TableHead>
                    <TableHead className="text-right">Coût employeur</TableHead>
                    <TableHead className="text-right">Part</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {d.parStructure.map((s) => (
                    <TableRow key={s.code}>
                      <TableCell>
                        <span className="font-medium">{s.code}</span> <span className="text-xs text-muted-foreground">{s.libelle}</span>
                      </TableCell>
                      <TableCell className="text-right">{s.agents}</TableCell>
                      <TableCell className="hidden md:table-cell text-right">{usd0(s.netAPayer)}</TableCell>
                      <TableCell className="text-right">{usd0(s.coutEmployeur)}</TableCell>
                      <TableCell className="text-right">{nombre(s.part, 1)} %</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Obligations sociales et fiscales{d ? ` · ${d.mois}` : ''}</CardTitle>
              <CardDescription>Montants dus au titre de la dernière paie calculée (états CNSS et IPR téléchargeables depuis le calcul).</CardDescription>
            </CardHeader>
            <CardContent>
              {!o ? (
                <Vide message="Aucune paie calculée." />
              ) : (
                <ul className="divide-y text-sm">
                  {[
                    ['CNSS · part salariale', o.cnssSalariale],
                    ['CNSS · part patronale', o.cnssPatronale],
                    ['INPP', o.inpp],
                    ['ONEM', o.onem],
                    [`Impôt professionnel (${nombre(o.iprCdf, 0)} CDF)`, o.iprUsd],
                    ['Retenues des prêts sociaux (recouvrement interne)', o.retenuesPrets],
                  ].map(([l, m]) => (
                    <li key={String(l)} className="flex items-center justify-between py-1.5">
                      <span>{l}</span>
                      <span className="font-medium">{usd(Number(m))}</span>
                    </li>
                  ))}
                  <li className="flex items-center justify-between py-1.5 font-semibold">
                    <span>Total à reverser</span>
                    <span>{usd(o.total)}</span>
                  </li>
                </ul>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Effectifs par catégorie</CardTitle>
              <CardDescription>Agents actifs payés par le barème (mandataires et membres du Conseil hors paie).</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              {data.effectifs.parCategorie.map((c) => (
                <Badge key={c.categorie} variant="outline" className="text-sm">
                  {c.libelle} · {c.agents}
                </Badge>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

/** Barres mensuelles (coût employeur en fond, net à payer devant), sans bibliothèque. */
function Barres({ paies }: { paies: ResumePaiePilotage[] }) {
  const max = Math.max(1, ...paies.map((p) => p.coutEmployeur));
  return (
    <div className="flex h-44 items-end gap-2 rounded-md border bg-muted/20 p-3" data-testid="barres-paie">
      {paies.map((p) => (
        <div key={p.id} className="flex min-w-0 flex-1 flex-col items-center gap-1" title={`${p.mois} · net ${usd(p.netAPayer)} · coût employeur ${usd(p.coutEmployeur)}`}>
          <div className="relative flex w-full items-end justify-center" style={{ height: '120px' }}>
            <div className="absolute bottom-0 w-3/4 rounded-t bg-slate-200" style={{ height: `${Math.max(2, (p.coutEmployeur / max) * 100)}%` }} />
            <div className={cn('absolute bottom-0 w-1/2 rounded-t', p.statut === 'CLOTUREE' ? 'bg-emerald-600' : 'bg-primary/70')} style={{ height: `${Math.max(2, (p.netAPayer / max) * 100)}%` }} />
          </div>
          <div className="truncate text-[10px] text-muted-foreground">{p.mois.slice(5)}/{p.mois.slice(2, 4)}</div>
        </div>
      ))}
    </div>
  );
}
